import { formatDate } from "@clipboard-health/date-time";
import { subMonths } from "date-fns";
import pluralize from "pluralize";

import { TB_TEST_TYPE, type TbTestType } from "./types";

export const TB_TEST_DATE_VALIDITY_MONTHS: Record<TbTestType, number> = {
  [TB_TEST_TYPE.TUBERCULIN_SKIN_TEST]: 12,
  [TB_TEST_TYPE.TB_BLOOD_TEST]: 12,
  [TB_TEST_TYPE.CHEST_X_RAY]: 60,
};

export const FOOTER_BUTTON_MIN_WIDTH = 120;

/**
 * Upload flow identifier used in standard document upload analytics events
 * (UPLOAD_DOCUMENT_SUCCESS / UPLOAD_DOCUMENT_FAILURE) to distinguish guided
 * uploads from the normal document upload flow.
 */
export const TB_TEST_GUIDED_UPLOAD_ANALYTICS_FLOW = "guided";

export const TB_TEST_TYPE_LABELS: Record<TbTestType, string> = {
  [TB_TEST_TYPE.TUBERCULIN_SKIN_TEST]: "Tuberculin Skin Test",
  [TB_TEST_TYPE.TB_BLOOD_TEST]: "TB Blood Test",
  [TB_TEST_TYPE.CHEST_X_RAY]: "Chest X-ray",
};

export const TB_TEST_TYPE_DESCRIPTIONS: Record<TbTestType, string | undefined> = {
  [TB_TEST_TYPE.TUBERCULIN_SKIN_TEST]: undefined,
  [TB_TEST_TYPE.TB_BLOOD_TEST]: undefined,
  [TB_TEST_TYPE.CHEST_X_RAY]: "Required if you've ever tested positive for TB",
};

export const TB_TEST_UPLOAD_CALLOUT =
  "Downloading from a portal or health app? Get the full report instead of a screenshot so all the required information is included.";

export const TB_TEST_TYPE_OPTIONS: TbTestType[] = [
  TB_TEST_TYPE.TUBERCULIN_SKIN_TEST,
  TB_TEST_TYPE.TB_BLOOD_TEST,
  TB_TEST_TYPE.CHEST_X_RAY,
];

/** Returns the earliest acceptable test date based on the test type's validity period. */
export function getMinimumTestDate(testType: TbTestType): Date {
  return subMonths(new Date(), TB_TEST_DATE_VALIDITY_MONTHS[testType]);
}

/** Formats the validity period as a human-readable string, e.g. "12 months" or "5 years". */
export function formatValidityPeriod(testType: TbTestType): string {
  const months = TB_TEST_DATE_VALIDITY_MONTHS[testType];
  if (months > 12 && months % 12 === 0) {
    const years = months / 12;
    return `${years} ${pluralize("year", years)}`;
  }
  return `${months} ${pluralize("month", months)}`;
}

/** Returns the checklist items for the instructions step, tailored to the given test type. */
export function getTbTestChecklist(testType: TbTestType): string[] {
  const formattedMinimumDate = formatDate(getMinimumTestDate(testType));

  const items = [
    "First and last name (as it appears on Clipboard)",
    'Negative test result or "0" induration',
    `Test date after ${formattedMinimumDate}`,
  ];

  if (testType === TB_TEST_TYPE.TUBERCULIN_SKIN_TEST) {
    items.push("Result read 48–72 hours after administration");
  }

  items.push("Provider's signature or clinic information (name, phone, and address)");

  return items;
}
